// [QR-TABLES] Écran TABLES : une carte par table, couleur = état, une action par commande
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, QrCode, Check, Banknote, Smartphone, UtensilsCrossed, X, Loader2, RefreshCw, Trash2, Bell, Receipt, Volume2, VolumeX } from "lucide-react";
import { playBeep } from "../lib/beep";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { PAYMENT_OPERATORS, getOperator, enabledOperators } from "../lib/payments";

const STATE = {
  libre: { label: "Libre", bg: "rgba(34,197,94,0.12)", border: "#22C55E", dot: "#22C55E" },
  en_cours: { label: "En cours", bg: "rgba(250,204,21,0.12)", border: "#FACC15", dot: "#FACC15" },
  a_encaisser: { label: "À encaisser", bg: "rgba(239,68,68,0.14)", border: "#EF4444", dot: "#EF4444" },
};

export default function Tables() {
  const { getAuthHeaders } = useAuth();
  const [tables, setTables] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calls, setCalls] = useState([]);
  const [cancelCount, setCancelCount] = useState(0);
  const [sound, setSound] = useState(() => localStorage.getItem("tables-sound") !== "off");
  const seenRef = useRef({ calls: null, orders: null });
  const userRole = localStorage.getItem("userRole") || "owner";
  const boutique = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutique.currencySymbol || "€";
  const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} ${devise}`;

  const load = useCallback(async () => {
    try {
      const [t, s, c] = await Promise.all([
        axios.get(`${API}/tables`, getAuthHeaders()),
        axios.get(`${API}/settings`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API}/tables/cancellations?days=1`, getAuthHeaders()).catch(() => ({ data: null })),
      ]);
      if (c.data) setCancelCount(c.data.count || 0);
      setTables(t.data.tables);
      const pending = t.data.pending_calls || [];
      setCalls(pending);
      // [BIP] nouvel appel ou nouvelle commande / modification → son + vibration
      const callIds = new Set(pending.map((c) => c.id));
      const orderKeys = new Set(t.data.tables.flatMap((x) => x.orders.map((o) => `${o.id}:${o.updated_at}`)));
      const prev = seenRef.current;
      if (prev.calls && (localStorage.getItem("tables-sound") !== "off")) {
        if ([...callIds].some((id) => !prev.calls.has(id))) playBeep("call");
        else if ([...orderKeys].some((k) => !prev.orders.has(k))) playBeep("order");
      }
      seenRef.current = { calls: callIds, orders: orderKeys };
      if (s.data) setSettings(s.data);
      if (selected) setSelected(t.data.tables.find((x) => (x.id || x.name) === (selected.id || selected.name)) || null);
    } catch { toast.error("Impossible de charger les tables"); }
    finally { setLoading(false); }
  }, [getAuthHeaders, selected]);

  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  const toggleSound = () => { const v = !sound; setSound(v); localStorage.setItem("tables-sound", v ? "on" : "off"); if (v) playBeep("call"); };
  const callDone = (id) => act(() => axios.post(`${API}/tables/calls/${id}/done`, {}, getAuthHeaders()));

  const addTables = async (count) => {
    try { await axios.post(`${API}/tables`, { count }, getAuthHeaders()); toast.success(`${count} table(s) ajoutée(s)`); load(); }
    catch { toast.error("Erreur"); }
  };
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const toCollect = tables.filter((t) => t.state === "a_encaisser").length;

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="tables-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl font-bold flex items-center gap-2"><UtensilsCrossed className="w-6 h-6" /> Tables</h1>
          <p className="text-sm text-muted-foreground">{toCollect > 0 ? `${toCollect} table(s) à encaisser` : "Tout est encaissé"} · {tables.filter((t) => !t.virtual).length} tables</p>
        </div>
        {cancelCount > 0 && userRole === "owner" && (
          <Button variant="outline" asChild className="border-red-500/50 text-red-500 hover:text-red-500" data-testid="cancellations-link"><Link to="/admin/cancellations">− {cancelCount} annulation{cancelCount > 1 ? "s" : ""} aujourd'hui</Link></Button>
        )}
        <Button variant="outline" asChild data-testid="qr-sheet-link"><Link to="/admin/tables/qr"><QrCode className="w-4 h-4 mr-2" /> QR codes</Link></Button>
        {userRole === "owner" && <Button onClick={() => addTables(1)} data-testid="add-table-btn"><Plus className="w-4 h-4 mr-1" /> Table</Button>}
        <Button variant="ghost" size="icon" onClick={toggleSound} title={sound ? "Son activé" : "Son coupé"} data-testid="tables-sound-btn">{sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}</Button>
        <Button variant="ghost" size="icon" onClick={load} data-testid="tables-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* [APPEL-SERVEUSE] appels en attente */}
      {calls.length > 0 && (
        <div className="space-y-2" data-testid="calls-banner">
          {calls.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-2xl px-4 py-3 border-2" style={{ borderColor: "#FF4500", background: "rgba(255,69,0,0.10)" }} data-testid={`call-${c.id}`}>
              <span className="w-10 h-10 rounded-full flex items-center justify-center text-white animate-pulse" style={{ background: "#FF4500" }}>{c.type === "addition" ? <Receipt className="w-5 h-5" /> : <Bell className="w-5 h-5" />}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold">{c.table_name} <span className="font-normal">· {c.type === "addition" ? "demande l'addition" : "appelle la serveuse"}</span></p>
                <p className="text-xs text-muted-foreground">{c.message ? `« ${c.message} » · ` : ""}{(() => { const m = Math.round((Date.now() - new Date(c.created_at)) / 60000); return m < 1 ? "à l'instant" : `il y a ${m} min`; })()}</p>
              </div>
              {c.type === "addition" && <Button size="sm" variant="outline" onClick={() => { callDone(c.id); setSelected(tables.find((t) => t.id === c.table_id) || null); }} data-testid={`call-open-${c.id}`}>Encaisser</Button>}
              <Button size="sm" style={{ background: "#FF4500" }} className="text-white" disabled={busy} onClick={() => callDone(c.id)} data-testid={`call-done-${c.id}`}><Check className="w-4 h-4 mr-1" /> J'y vais</Button>
            </div>
          ))}
        </div>
      )}

      {tables.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center space-y-4" data-testid="tables-empty">
          <p className="text-muted-foreground">Aucune table. Créez vos tables, imprimez les QR codes, posez-les sur les tables.</p>
          <div className="flex justify-center gap-2">{[4, 8, 12].map((n) => <Button key={n} variant="outline" onClick={() => addTables(n)} data-testid={`add-tables-${n}`}>{n} tables</Button>)}</div>
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
          {tables.map((t) => {
            const st = STATE[t.state];
            const o = t.orders[0];
            return (
              <button key={t.id || t.name} onClick={() => setSelected(t)} className="rounded-2xl p-4 text-left border-2 transition-transform active:scale-95 min-h-[120px] flex flex-col" style={{ background: st.bg, borderColor: st.border }} data-testid={`table-card-${t.id || t.name}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-base">{t.name}</span>
                  {t.calls?.length > 0
                    ? <span className="w-6 h-6 rounded-full flex items-center justify-center text-white animate-bounce" style={{ background: "#FF4500" }} data-testid={`table-call-${t.id}`}><Bell className="w-3.5 h-3.5" /></span>
                    : <span className="w-3 h-3 rounded-full" style={{ background: st.dot, boxShadow: `0 0 10px ${st.dot}` }} />}
                </div>
                <span className="text-xs mt-0.5" style={{ color: st.border }}>{st.label}</span>
                {o && (
                  <div className="mt-auto pt-2 text-xs">
                    <p className="font-bold text-sm">{fmt(t.open_total)}</p>
                    <p className="text-muted-foreground truncate">{t.orders.reduce((s, x) => s + x.items.reduce((a, i) => a + i.quantity, 0), 0)} article(s) · {o.payment_method === "cash" ? "Espèces" : o.payment_method === "card" ? (getOperator(o.payment_operator)?.short || "Mobile") : "Paiement ?"}</p>
                    {o.client_confirmed && <p className="text-green-500 mt-0.5">✓ client</p>}
                    {t.orders.some((x) => x.has_updates) && <p className="mt-0.5 font-semibold" style={{ color: "#FF4500" }} data-testid={`table-updates-${t.id}`}>● Modifiée</p>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="table-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">{selected.name} <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: STATE[selected.state].bg, color: STATE[selected.state].border }}>{STATE[selected.state].label}</span></SheetTitle>
                <SheetDescription>{selected.orders.length === 0 ? "Aucune commande en cours." : `${selected.orders.length} commande(s) · ${fmt(selected.open_total)}`}</SheetDescription>
              </SheetHeader>

              <div className="mt-4 space-y-4">
                {selected.orders.length === 0 && (
                  <div className="space-y-3">
                    <Button asChild className="w-full" data-testid="take-order-btn"><Link to={`/admin/service?table=${selected.id || ""}&name=${encodeURIComponent(selected.name)}`}><UtensilsCrossed className="w-4 h-4 mr-2" /> Prendre la commande</Link></Button>
                    {userRole === "owner" && selected.id && (
                      <Button variant="ghost" className="w-full text-destructive" onClick={() => act(() => axios.delete(`${API}/tables/${selected.id}`, getAuthHeaders()), "Table supprimée").then(() => setSelected(null))} data-testid="delete-table-btn"><Trash2 className="w-4 h-4 mr-2" /> Supprimer cette table</Button>
                    )}
                  </div>
                )}
                {selected.orders.map((o) => (
                  <OrderCard key={o.id} order={o} fmt={fmt} busy={busy} operators={enabledOperators(settings)}
                    onServe={() => act(() => axios.post(`${API}/tables/orders/${o.id}/serve`, {}, getAuthHeaders()), "Marquée servie")}
                    onCollect={(payload) => act(() => axios.post(`${API}/tables/orders/${o.id}/collect`, payload, getAuthHeaders()), "Encaissé ✓")}
                    onCancel={() => act(() => axios.post(`${API}/tables/orders/${o.id}/cancel`, {}, getAuthHeaders()), "Commande annulée")}
                    onSeen={() => axios.post(`${API}/tables/orders/${o.id}/seen`, {}, getAuthHeaders()).catch(() => {})}
                  />
                ))}
                {selected.orders.length > 0 && (
                  <Button variant="outline" asChild className="w-full" data-testid="add-to-table-btn"><Link to={`/admin/service?table=${selected.id || ""}&name=${encodeURIComponent(selected.name)}`}><Plus className="w-4 h-4 mr-2" /> Ajouter une commande</Link></Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function OrderCard({ order: o, fmt, busy, operators, onServe, onCollect, onCancel, onSeen }) {
  const [method, setMethod] = useState(o.payment_method || null);
  const [operator, setOperator] = useState(o.payment_operator || null);
  useEffect(() => { setMethod(o.payment_method || null); setOperator(o.payment_operator || null); }, [o.payment_method, o.payment_operator]);
  useEffect(() => { if (o.has_updates) onSeen?.(); }, [o.has_updates]); // eslint-disable-line react-hooks/exhaustive-deps
  const lastAddition = o.additions?.length ? o.additions[o.additions.length - 1] : null;
  const [confirmCancel, setConfirmCancel] = useState(false);
  const needOperator = method === "card" && !operator;
  const label = method === "cash" ? "espèces" : method === "card" ? (getOperator(operator)?.label || "Mobile Money") : "";

  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid={`order-card-${o.id}`}>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{o.source === "client" ? "📱 Commandé par le client" : `🧑‍🍳 ${o.serveur?.name || "Serveuse"}`}</span>
        <span>{new Date(o.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      {(o.has_updates || lastAddition) && (
        <div className="rounded-lg px-3 py-2 text-xs flex items-center gap-2" style={{ background: "rgba(255,69,0,0.12)", color: "#FF4500" }} data-testid={`order-updates-${o.id}`}>
          <span className="w-2 h-2 rounded-full bg-[#FF4500] animate-pulse" />
          {lastAddition && o.last_change?.type !== "edit"
            ? <span><b>Ajout {new Date(lastAddition.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</b> : {lastAddition.items.map((i) => `+${i.quantity} ${i.product_name}`).join(", ")}</span>
            : <span>Commande modifiée par le client{o.last_change?.at ? ` à ${new Date(o.last_change.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : ""} — vérifiez les quantités</span>}
        </div>
      )}
      <div className="space-y-1 text-sm">
        {o.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.quantity}× {i.product_name}{i.locked && <span className="ml-1 text-muted-foreground text-xs">✓ servi</span>}</span><span className="font-medium">{fmt(i.total)}</span></div>)}
        {o.note && <p className="text-xs italic text-muted-foreground">« {o.note} »</p>}
      </div>
      <div className="flex justify-between font-bold border-t pt-2"><span>Total</span><span data-testid={`order-total-${o.id}`}>{fmt(o.total)}</span></div>

      {/* Double validation */}
      <div className="flex gap-2 text-xs">
        <span className={`px-2 py-1 rounded-full ${o.client_confirmed ? "bg-green-500/15 text-green-500" : "bg-muted text-muted-foreground"}`} data-testid={`client-confirmed-${o.id}`}>{o.client_confirmed ? "✓ Client : " + (o.payment_method === "cash" ? "espèces" : getOperator(o.payment_operator)?.label || "Mobile Money") : "Client : pas encore confirmé"}</span>
        <span className={`px-2 py-1 rounded-full ${o.status === "servie" ? "bg-green-500/15 text-green-500" : "bg-muted text-muted-foreground"}`}>{o.status === "servie" ? "✓ Servie" : "À servir"}</span>
      </div>

      {o.status === "en_attente" && <Button variant="outline" className="w-full" disabled={busy} onClick={onServe} data-testid={`serve-btn-${o.id}`}><Check className="w-4 h-4 mr-2" /> Servi</Button>}

      {/* Paiement */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { setMethod("cash"); setOperator(null); }} className="py-2.5 rounded-lg text-white text-xs font-bold flex items-center justify-center gap-1 border-2" style={{ background: "#111", borderColor: method === "cash" ? "#FF4500" : "#2A2A2A" }} data-testid={`pay-cash-${o.id}`}><Banknote className="w-4 h-4" /> Espèces</button>
        <button onClick={() => setMethod("card")} className="py-2.5 rounded-lg text-white text-xs font-bold flex items-center justify-center gap-1 border-2" style={{ background: method === "card" ? "#FF4500" : "#22C55E", borderColor: method === "card" ? "#FF4500" : "#22C55E" }} data-testid={`pay-card-${o.id}`}><Smartphone className="w-4 h-4" /> Mobile Money</button>
      </div>
      {method === "card" && (
        <div className="grid grid-cols-4 gap-1.5">
          {operators.map((op) => <button key={op.id} onClick={() => setOperator(op.id)} className="py-2 rounded-lg text-[10px] font-bold border-2" style={{ background: operator === op.id ? "#FF4500" : op.color, color: operator === op.id ? "#fff" : op.text, borderColor: operator === op.id ? "#fff" : op.color }} data-testid={`pay-op-${op.id}-${o.id}`}>{op.short}</button>)}
        </div>
      )}
      <Button className="w-full h-12 text-base font-bold" style={{ background: "#FF4500" }} disabled={busy || !method || needOperator} onClick={() => onCollect({ payment_method: method, payment_operator: operator })} data-testid={`collect-btn-${o.id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>J'ai encaissé {fmt(o.total)} {label && `en ${label}`}</>}
      </Button>

      {!confirmCancel ? (
        <button onClick={() => setConfirmCancel(true)} className="w-full text-xs text-muted-foreground hover:text-destructive flex items-center justify-center gap-1" data-testid={`cancel-btn-${o.id}`}><X className="w-3 h-3" /> Annuler la commande</button>
      ) : (
        <div className="flex gap-2"><Button variant="destructive" size="sm" className="flex-1" onClick={onCancel} data-testid={`cancel-confirm-${o.id}`}>Oui, annuler</Button><Button variant="outline" size="sm" className="flex-1" onClick={() => setConfirmCancel(false)}>Non</Button></div>
      )}
    </div>
  );
}

export { PAYMENT_OPERATORS };
